export const ADMIN_TOP_LEVEL_TABS = [
  'overview',
  'accounts',
  'billing',
  'communications',
  'content',
  'training',
  'marketplace',
  'certificates',
  'scan-engine',
  'fleet-operations',
  'logo-processing',
  'ai-agent',
  'insights',
  'scoring',
  'platform',
] as const;

export type AdminTopLevelTab = (typeof ADMIN_TOP_LEVEL_TABS)[number];
export type AdminTab = AdminTopLevelTab;

export const ADMIN_SUB_TABS = {
  accounts: ['users', 'organisations', 'supplierorgs', 'msp', 'breakglass'],
  certificates: ['all', 'cyber-essentials', 'logos'],
  marketplace: ['listings', 'supplier-profile', 'dependencies', 'signatures'],
  communications: ['emails', 'email-queue', 'invitations', 'support-notices'],
  content: ['questionnaires', 'glossary', 'breach-feed', 'advisories', 'dependencies', 'supply-chain-briefing', 'enterprise-sso'],
  platform: ['docs', 'cron', 'api-keys', 'api-budgets', 'integrations', 'security', 'feature-flags', 'notification-coverage', 'audit-coverage', 'audit-log'],
} as const;

export type AdminSubTabGroup = keyof typeof ADMIN_SUB_TABS;
export type AdminSubTab = (typeof ADMIN_SUB_TABS)[AdminSubTabGroup][number];

const ADMIN_SUB_TAB_DEFAULTS: Record<AdminSubTabGroup, AdminSubTab> = {
  accounts: 'users',
  certificates: 'all',
  marketplace: 'listings',
  communications: 'emails',
  content: 'questionnaires',
  platform: 'docs',
};

// ENT-5191: renamed tabs keep their old slug as an alias so existing deep links
// (Slack, runbooks) still resolve. Admin rewrites the URL to the canonical slug.
const ADMIN_LEGACY_TAB_ALIASES: ReadonlyMap<string, AdminTopLevelTab> = new Map([
  ['scan-engine-operations', 'fleet-operations'],
]);

export function getAdminTopLevelTab(value: string | null | undefined): AdminTopLevelTab {
  const aliasTarget = value ? ADMIN_LEGACY_TAB_ALIASES.get(value) : undefined;
  if (aliasTarget) {
    return aliasTarget;
  }
  return ADMIN_TOP_LEVEL_TABS.includes(value as AdminTopLevelTab) ? (value as AdminTopLevelTab) : 'overview';
}

/**
 * Returns the search params with a legacy `tab` alias replaced by its canonical
 * slug (all other params preserved), or `null` when no redirect is needed.
 */
export function getAdminLegacyTabRedirect(searchParams: URLSearchParams): URLSearchParams | null {
  const tab = searchParams.get('tab');
  const aliasTarget = tab ? ADMIN_LEGACY_TAB_ALIASES.get(tab) : undefined;
  if (!aliasTarget) {
    return null;
  }
  const nextParams = new URLSearchParams(searchParams);
  nextParams.set('tab', aliasTarget);
  return nextParams;
}

export function getAdminActiveTab(value: string | null | undefined): AdminTab {
  return getAdminTopLevelTab(value);
}

export function getAdminSubTab(
  tab: AdminTopLevelTab,
  value: string | null | undefined,
): AdminSubTab | null {
  if (!(tab in ADMIN_SUB_TABS)) {
    return null;
  }

  const subTabs = ADMIN_SUB_TABS[tab as AdminSubTabGroup];
  const candidate = value as AdminSubTab | null | undefined;

  if (candidate && subTabs.includes(candidate)) {
    return candidate;
  }

  return ADMIN_SUB_TAB_DEFAULTS[tab as AdminSubTabGroup];
}

export function getAdminTabUrl(tab: AdminTopLevelTab, subTab?: AdminSubTab | null) {
  if (!subTab || tab === 'overview') {
    return tab === 'overview' ? '/admin' : `/admin?tab=${tab}`;
  }

  return `/admin?tab=${tab}&subtab=${subTab}`;
}

export function clearAdminRecordPeekParams(searchParams: URLSearchParams) {
  const nextParams = new URLSearchParams(searchParams);
  nextParams.delete('peek');
  nextParams.delete('peekTab');
  // ENT-4073: peekField (e.g. identity-domain focus intent) is record-drawer state — clear it with
  // the other peek params so an abandoned intent can't focus a later record's field.
  nextParams.delete('peekField');
  return nextParams;
}
